<template>
  <InlineTypeformSurvey
    v-if="isTypeform && typeformId"
    :typeform-id="typeformId"
    :button-props="TypeformButtonProps"
    :button-text="
      isEntryOfContentType(item, 'link') ? item.fields.linkText : undefined
    "
  >
  </InlineTypeformSurvey>
  <LinkElement v-else-if="childProps" v-bind="childProps">
    <slot v-if="useSlot"></slot>
    <template v-if="!useSlot">
      {{
        isEntryOfContentType(item, 'download')
          ? item.fields.title
          : item.fields.linkText
      }}
    </template>
  </LinkElement>
</template>

<script setup lang="ts">
import { LinkElement } from '@hypercodestudio/basler-components';
import type { Props as LinkProps } from '@hypercodestudio/basler-components/dist/components/elements/LinkElement.vue';
import type { Props as ButtonProps } from '@hypercodestudio/basler-components/dist/components/elements/Button.vue';

import type { IDownload, ILink } from '~/lib/ContentfulService';
import { getLinkIconPosition } from '~/utils/getLinkIconPosition';
import { isEntryOfContentType } from '~/utils/guards/isEntryOfContentType';
import { isEmpty } from '~/utils/isEmpty';
import InlineTypeformSurvey from '~/components/global/lib/InlineTypeformSurvey.vue';
interface Props {
  item: ILink | IDownload;
  useSlot?: boolean;
}

const props = defineProps<Props>();

const hasDocument =
  isEntryOfContentType(props.item, 'link') && props.item.fields.documentId;
const hasSoftware =
  isEntryOfContentType(props.item, 'link') && props.item.fields.softwareId;

const childProps: Ref<LinkProps> = computed(() =>
  !hasDocument && !hasSoftware && isEntryOfContentType(props.item, 'download')
    ? getPropsForDownload(props.item)
    : getPropsForLink(props.item)
);

const TypeformButtonProps = computed(() =>
  isEntryOfContentType(props.item, 'link') &&
  !isEmpty(props.item.fields?.typeformForm)
    ? getPropsForTypeform(props.item)
    : undefined
);

const isTypeform = computed(() => {
  return (
    isEntryOfContentType(props.item, 'link') &&
    !isEmpty(props.item.fields?.typeformForm)
  );
});

const typeformId = computed(() => {
  if (isEntryOfContentType(props.item, 'link')) {
    return props.item.fields?.typeformForm || undefined;
  }
  return undefined;
});

function getPropsForLink(item: ILink): LinkProps {
  const link = useBuildLinkInterface(item);
  return {
    link,
    targetBlank: item.fields?.linkTarget === '_blank',
    size: item.fields?.linkStyleSize,
    styleType: item.fields?.linkStyle ?? 'default',
    iconName: item.fields?.linkIcon,
    iconType: getLinkIconPosition(item.fields?.linkIconPosition)
  };
}

function getPropsForDownload(item: IDownload): LinkProps {
  const link = useBuildLinkInterface(item);
  return {
    link,
    targetBlank: true,
    size: 'medium',
    styleType: 'default',
    iconType: 'no-icon'
  };
}
function getPropsForTypeform(item: ILink): ButtonProps {
  return {
    size: item.fields?.linkStyleSize,
    styleType: (item.fields?.linkStyle as any) ?? 'primary',
    iconName: item.fields?.linkIcon,
    iconType: getLinkIconPosition(item.fields?.linkIconPosition)
  };
}
</script>
